import React, { useState, Fragment, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { useNavigate, useParams } from "react-router-dom";

import {
  userPasswordRecovery,
  userSavePassword,
  userCheckResetPasswordHash,
} from "@/store/auth";

import AppInput from "@/components/AppInput";
import AppButton from "@/components/AppButton";
import AppLink from "@/components/AppLink";
import AppCard from "@/components/AppCard";
import AppText from "@/components/AppText";
import AppBox from "@/components/AppBox";

interface FormState {
  email: string;
  password: string;
  confirmPassword: string;
}

interface ErrorState {
  email: string;
  password: string;
  confirmPassword: string;
}

const Recovery: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const params = useParams<{ hash?: string }>();

  const { isValidHash, status } = useAppSelector((state) => state.auth);

  const [form, setForm] = useState<FormState>({
    email: "",
    password: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState<ErrorState>({
    email: "",
    password: "",
    confirmPassword: "",
  });

  const [resetRequestSent, setResetRequestSent] = useState(false);

  useEffect(() => {
    if (params.hash && !isValidHash) {
      checkHash(params.hash);
    }
  }, [params, isValidHash]);

  const checkHash = async (hash: string) => {
    await dispatch(userCheckResetPasswordHash(hash));
  };

  const handleChange =
    (field: keyof FormState) =>
    (value: string): void => {
      if (errors[field]) {
        setErrors((state) => ({
          ...state,
          [field]: "",
        }));
      }
      setForm((state) => ({
        ...state,
        [field]: value,
      }));
    };

  const handleSubmit = async () => {
    if (!form.email) {
      setErrors((state) => ({ ...state, email: "Email is required!" }));
      return;
    }

    const res = await dispatch(
      userPasswordRecovery({
        email: form.email,
        link: `${window.location.origin}/recovery/{hash}`,
      })
    );
    if (!res) {
      setResetRequestSent(true);
    }
  };

  const handleSubmitPassword = async () => {
    const res = await dispatch(
      userSavePassword({
        password: form.password,
        password_confirmation: form.confirmPassword,
        hash: params.hash!,
      })
    );

    navigate("/");
  };

  return (
    <AppCard>
      {!params.hash ? (
        !resetRequestSent ? (
          <AppBox column gap={4}>
            <AppInput
              type="email"
              label="Email Address"
              fullWidth
              onChange={handleChange("email")}
              value={form.email}
              error={errors.email}
              onPressEnter={handleSubmit}
            />
            <AppButton onClick={handleSubmit} isLoading={status === "loading"}>
              Reset password
            </AppButton>
            <AppLink to="/">Go Back</AppLink>
          </AppBox>
        ) : (
          <AppBox column center gap={4}>
            <AppText align="center">
              Your reset password request was sent!<br />
              Check your mailbox and follow the instructions.
            </AppText>
            <AppLink to="/">Go Back</AppLink>
          </AppBox>
        )
      ) : isValidHash ? (
        <AppBox column center gap={4}>
          <AppInput
            type="password"
            label="New Password"
            fullWidth
            onChange={handleChange("password")}
            value={form.password}
            onPressEnter={handleSubmitPassword}
          />
          <AppInput
            type="password"
            label="Confirm Password"
            fullWidth
            onChange={handleChange("confirmPassword")}
            value={form.confirmPassword}
            onPressEnter={handleSubmitPassword}
          />
          <AppButton
            onClick={handleSubmitPassword}
            isLoading={status === "loading"}
          >
            Save new password
          </AppButton>
          <AppLink to="/">Go Back</AppLink>
        </AppBox>
      ) : (
        <AppBox column center gap={4}>
          <AppText align="center">
            Your reset password link has expired.<br />
            Try to request a password reset again.
          </AppText>
          <AppLink to="/">Go Back</AppLink>
        </AppBox>
      )}
    </AppCard>
  );
};

export default Recovery;